import { BufferGeometry, Line, Vector3, type TypedArray } from 'three'
import { arePointsColinear } from './vector-3'

/**
 * Simplifies a line by removing intermediate points that create colinear lines.
 *
 * @param line - The line to simplify
 * @returns A new simplified line with colinear intermediate points removed
 */
export const simplifyLine = (line: Line): Line => {
  const simplified = line.clone(true)

  const points: Vector3[] = []

  if (simplified.geometry.attributes.position.array.length < 6) {
    return line.clone(true)
  }

  for (let i = 0; i < simplified.geometry.attributes.position.array.length; i += 3) {
    points.push(
      new Vector3(
        simplified.geometry.attributes.position.array[i],
        simplified.geometry.attributes.position.array[i + 1],
        simplified.geometry.attributes.position.array[i + 2],
      ),
    )
  }

  const filteredPoints: Vector3[] = [points[0]]

  for (let i = 1; i < points.length - 1; i++) {
    const a = points[i - 1]
    const b = points[i]
    const c = points[i + 1]

    if (!arePointsColinear(a, b, c)) {
      filteredPoints.push(b)
    }
  }

  filteredPoints.push(points.at(-1)!)

  simplified.geometry = new BufferGeometry().setFromPoints(filteredPoints)

  return simplified
}

/**
 * Checks if two lines are connected (share an endpoint).
 *
 * @param line1 - First line
 * @param line2 - Second line
 * @param epsilon - Tolerance for endpoint comparison (default: 0.001)
 * @returns true if lines share an endpoint, false otherwise
 */
export const areLinesConnected = (line1: Line, line2: Line, epsilon = 0.001): boolean => {
  const pos1 = line1.geometry.attributes.position.array
  const pos2 = line2.geometry.attributes.position.array

  // Get endpoints of line1
  const line1Start = new Vector3(pos1[0], pos1[1], pos1[2])
  const line1End = new Vector3(pos1[pos1.length - 3], pos1[pos1.length - 2], pos1[pos1.length - 1])

  // Get endpoints of line2
  const line2Start = new Vector3(pos2[0], pos2[1], pos2[2])
  const line2End = new Vector3(pos2[pos2.length - 3], pos2[pos2.length - 2], pos2[pos2.length - 1])

  // Check if any endpoint touches another endpoint
  return (
    line1End.distanceTo(line2Start) < epsilon
    || line1End.distanceTo(line2End) < epsilon
    || line1Start.distanceTo(line2Start) < epsilon
    || line1Start.distanceTo(line2End) < epsilon
  )
}

/**
 * Merges two connected lines into a single line by combining their points in the correct order.
 * Automatically determines the proper orientation based on which endpoints are connected.
 *
 * @param line1 - First line to merge
 * @param line2 - Second line to merge
 * @returns A new line containing all points from both lines in the correct order, with duplicate connection points removed
 */
export const mergeLines = (line1: Line, line2: Line): Line => {
  const pos1 = line1.geometry.attributes.position.array
  const pos2 = line2.geometry.attributes.position.array

  const points1: Vector3[] = []
  const points2: Vector3[] = []

  for (let i = 0; i < pos1.length; i += 3) {
    points1.push(new Vector3(pos1[i], pos1[i + 1], pos1[i + 2]))
  }

  for (let i = 0; i < pos2.length; i += 3) {
    points2.push(new Vector3(pos2[i], pos2[i + 1], pos2[i + 2]))
  }

  const epsilon = 0.001
  const line1Start = points1[0]
  const line1End = points1.at(-1)!
  const line2Start = points2[0]
  const line2End = points2.at(-1)!

  let mergedPoints: Vector3[]

  // Determine the correct order to merge the lines
  if (line1End.distanceTo(line2Start) < epsilon) {
    // line1 -> line2 (normal case)
    mergedPoints = [...points1, ...points2.slice(1)]
  }
  else if (line1End.distanceTo(line2End) < epsilon) {
    // line1 -> reversed line2
    mergedPoints = [...points1, ...points2.slice(0, -1).reverse()]
  }
  else if (line1Start.distanceTo(line2Start) < epsilon) {
    // reversed line1 -> line2
    mergedPoints = [...points1.slice(1).reverse(), ...points2]
  }
  else if (line1Start.distanceTo(line2End) < epsilon) {
    // line2 -> line1
    mergedPoints = [...points2, ...points1.slice(1)]
  }
  else {
    // Fallback: just concatenate if no clear connection (shouldn't happen)
    mergedPoints = [...points1, ...points2]
  }

  const mergedLine = line1.clone()
  mergedLine.geometry = new BufferGeometry().setFromPoints(mergedPoints)

  return mergedLine
}

/**
 * Calculates the total length of a line by summing the distances between consecutive points.
 *
 * @param line - The line to measure
 * @returns The total length of the line
 */
export const getLineLength = (line: Line): number => {
  const positions = line.geometry.attributes.position.array
  let totalLength = 0

  for (let i = 0; i < positions.length - 3; i += 3) {
    const p1 = new Vector3(positions[i], positions[i + 1], positions[i + 2])
    const p2 = new Vector3(positions[i + 3], positions[i + 4], positions[i + 5])
    totalLength += p1.distanceTo(p2)
  }

  return totalLength
}

/**
 * Calculates the 2D bounding box of a line from its position array.
 * Only considers x and y coordinates, ignoring z.
 *
 * @param posArray - The typed array containing position data [x, y, z, x, y, z, ...]
 * @returns Object containing minX, minY, maxX, maxY coordinates of the bounding box
 */
export const getLineBoundingBox = (posArray: TypedArray) => {
  let minX = Infinity, minY = Infinity
  let maxX = -Infinity, maxY = -Infinity

  for (let i = 0; i < posArray.length; i += 3) {
    const x = posArray[i]
    const y = posArray[i + 1]
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }

  return { minX, minY, maxX, maxY }
}

/**
 * Compares two lines by checking if their bounding boxes are similar within a tolerance.
 * Two lines are considered the same if all four bounding box coordinates (minX, minY, maxX, maxY)
 * are within epsilon distance of each other.
 *
 * @param line1 - First line to compare
 * @param line2 - Second line to compare
 * @param epsilon - Maximum allowed difference for bounding box coordinates (default: 20)
 * @returns true if the bounding boxes match within tolerance, false otherwise
 */
export const isSameLine = (line1: Line, line2: Line, epsilon = 20) => {
  const pos1 = line1.geometry.attributes.position.array
  const pos2 = line2.geometry.attributes.position.array

  // Extract bounding boxes
  const bbox1 = getLineBoundingBox(pos1)
  const bbox2 = getLineBoundingBox(pos2)

  // Compare bounding boxes with tolerance
  return (
    Math.abs(bbox1.minX - bbox2.minX) < epsilon
    && Math.abs(bbox1.minY - bbox2.minY) < epsilon
    && Math.abs(bbox1.maxX - bbox2.maxX) < epsilon
    && Math.abs(bbox1.maxY - bbox2.maxY) < epsilon
  )
}

/**
 * Sorts and groups randomly ordered lines by connecting them end-to-end.
 * Lines that can be connected together are grouped in the same array.
 * Lines that cannot connect to any other lines are placed in separate groups.
 *
 * The algorithm:
 * 1. Creates groups of connected lines by iteratively finding and merging connected lines
 * 2. Each group represents a continuous path of connected line segments
 * 3. Lines that share endpoints (within epsilon tolerance) are merged together
 * 4. Isolated lines or separate paths are returned as distinct groups
 *
 * @param lines - Array of lines in random order to be sorted and grouped
 * @param epsilon - Tolerance for endpoint connection detection (default: 0.001)
 * @returns Array of line groups, where each group contains connected lines merged into single lines
 *
 * @example
 * ```typescript
 * const lines = [line1, line2, line3, line4];
 * const groups = sortLines(lines);
 * // groups[0] might contain [mergedLine1] (line1 + line2 connected)
 * // groups[1] might contain [mergedLine2] (line3 + line4 connected)
 * ```
 */
export const sortLines = (lines: Line[], epsilon = 0.001): Line[][] => {
  if (lines.length === 0) {
    return []
  }

  if (lines.length === 1) {
    return [[lines[0]]]
  }

  const remaining = [...lines]
  const groups: Line[][] = []

  while (remaining.length > 0) {
    // Start a new group with the first remaining line
    let currentLine = remaining.shift()!
    const currentGroup: Line[] = [currentLine]

    let foundConnection = true

    // Keep trying to connect lines until no more connections are found
    while (foundConnection) {
      foundConnection = false

      // Try to find a line that connects to the current line
      for (let i = 0; i < remaining.length; i++) {
        const candidateLine = remaining[i]

        if (areLinesConnected(currentLine, candidateLine, epsilon)) {
          // Merge the lines and remove the candidate from remaining
          currentLine = mergeLines(currentLine, candidateLine)
          remaining.splice(i, 1)
          foundConnection = true
          break
        }
      }
    }

    // Add the final merged line to the group
    currentGroup[0] = currentLine
    groups.push(currentGroup)
  }

  return groups
}

/**
 * Gets the 2D bounding box (XY plane) of a line, ignoring Z coordinates.
 *
 * @param line - The line to analyze
 * @returns Object containing minX, minY, maxX, maxY coordinates
 */
const getLineBoundingBoxXY = (line: Line) => {
  const positions = line.geometry.attributes.position.array
  return getLineBoundingBox(positions)
}

/**
 * Checks if two lines have the same 2D shape (same XY bounding box) within tolerance.
 *
 * @param line1 - First line to compare
 * @param line2 - Second line to compare
 * @param epsilon - Tolerance for bounding box comparison
 * @returns true if lines have similar XY bounding boxes, false otherwise
 */
const haveSameXYBoundingBox = (line1: Line, line2: Line, epsilon: number): boolean => {
  const bbox1 = getLineBoundingBoxXY(line1)
  const bbox2 = getLineBoundingBoxXY(line2)

  return (
    Math.abs(bbox1.minX - bbox2.minX) < epsilon
    && Math.abs(bbox1.minY - bbox2.minY) < epsilon
    && Math.abs(bbox1.maxX - bbox2.maxX) < epsilon
    && Math.abs(bbox1.maxY - bbox2.maxY) < epsilon
  )
}

/**
 * Groups identical lines that only differ in their Z coordinate (z-up axis).
 * Lines with the same XY bounding box (within epsilon tolerance) are grouped together,
 * regardless of their Z position.
 *
 * The algorithm:
 * 1. Compares each line's 2D bounding box (XY plane) with others
 * 2. Groups lines that have the same XY shape (within epsilon tolerance)
 * 3. Each group represents the same line repeated at different Z heights
 * 4. Lines that don't match any existing group create a new group
 *
 * @param lines - Array of lines to group by their XY shape
 * @param epsilon - Tolerance for XY bounding box comparison (default: 0.1)
 * @returns Array of line groups, where each group contains lines with identical XY shapes
 *
 * @example
 * ```typescript
 * // Same rectangle at different heights
 * const rect1 = createRectangle(0, 0, 10, 10, z: 0);
 * const rect2 = createRectangle(0, 0, 10, 10, z: 5);
 * const rect3 = createRectangle(0, 0, 10, 10, z: 10);
 * const groups = groupOverlappingLines([rect1, rect2, rect3], 0.1);
 * // groups[0] contains [rect1, rect2, rect3] (same XY shape, different Z)
 * ```
 */
export const groupOverlappingLines = (lines: Line[], epsilon = 0.1): Line[][] => {
  if (lines.length === 0) {
    return []
  }

  if (lines.length === 1) {
    return [[lines[0]]]
  }

  const groups: Line[][] = []
  const processed = new Set<number>()

  // For each line, find all lines with the same XY bounding box
  for (let i = 0; i < lines.length; i++) {
    if (processed.has(i)) continue

    const currentGroup: Line[] = [lines[i]]
    processed.add(i)

    // Check all remaining lines
    for (let j = i + 1; j < lines.length; j++) {
      if (processed.has(j)) continue

      if (haveSameXYBoundingBox(lines[i], lines[j], epsilon)) {
        currentGroup.push(lines[j])
        processed.add(j)
      }
    }

    groups.push(currentGroup)
  }

  return groups
}
